import type { ConciergeSidebarWidgetState, ConciergeSidebarResolvedIcons } from '@brandclub/bc-widgets';
import { ConciergeSidebarWidget, useStudioSupportSkills } from '@brandclub/bc-widgets';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAppConfig, useBrandAppConfig, useDomainConfig } from '../../api/cache/appConfigQueryOptions';
import { AnalyticsService, AnalyticsTrackingEvent } from '../../analytics';
import { useConversations, type ConversationItem } from '../hooks/useConversations';
import { useOptionalConciergeContext } from '../ConciergeContextProvider';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ConversationActionMenu, buildConversationMenuItems } from './ConversationActionMenu';
import { useIsMobile } from './ConciergeChatAreaV2/useIsMobile';
import '../styles/animations.css';
import { resolveBrandclubHostDomain } from '../../utils/domain';
import {
  IconChats,
  IconChevronUp,
  IconCompose,
  IconHome,
  IconLogout,
  IconProfile,
  IconPurchases,
  IconSidebar,
} from './ConciergeIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** User profile info for avatar display */
export interface ConciergeSidebarUserProfile {
  firstName?: string;
  lastName?: string;
  /** Avatar fallback when the user has no name (e.g. Shopify accounts without one). */
  email?: string;
  isSignedIn: boolean;
}

export interface ConciergeSidebarProps {
  onConversationSelect?: (conversationId: string, mode?: 'text' | 'voice') => void;
  onComposeClick?: () => void;
  onChatsClick?: () => void;
  currentConversationId?: string | null;
  userProfile?: ConciergeSidebarUserProfile;
  apiBaseUrl?: string;
  onSignOutClick?: () => void;
  onProfileClick?: () => void;
  onPurchasesClick?: () => void;
  onHomeClick?: () => void;
  brandLogoUrl?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  /** When true, renders as a mobile overlay (always expanded, no tw:hidden) */
  mobileOverlay?: boolean;
  /** Optional: called when user chooses "Share chat" from the chat history item menu */
  onShareChat?: (conversationId: string) => void;
  /** Optional: called when a logged-out user taps the "Sign in" link */
  onSignInClick?: () => void;
  /**
   * Conversation id currently in a live support takeover (if any). When set,
   * the matching row in the chat-history list renders a small pulsing emerald
   * dot to convey "you're talking to a human teammate here".
   */
  liveTakeoverActiveConversationId?: string | null;
}

// ---------------------------------------------------------------------------
// Font-weight keyword → number
// ---------------------------------------------------------------------------

const weightMap: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ---------------------------------------------------------------------------
// Timestamp formatter
// ---------------------------------------------------------------------------

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SidebarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isExpanded: boolean;
  ariaLabel: string;
  state: ConciergeSidebarWidgetState;
  resolveColor: (c: any, d?: string) => string;
}

/** A single row in the sidebar – icon on the left, optional label on the right. */
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, state, resolveColor }: SidebarButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="tw:relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        aria-label={ariaLabel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="tw:flex tw:items-center tw:h-10 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full tw:px-3 tw:gap-2"
      >
        <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-6">{icon}</div>
        {isExpanded && (
          <span
            className="tw:font-medium tw:whitespace-nowrap tw:overflow-hidden"
            style={{
              color: resolveColor(state.labelColor),
              fontSize: state.labelFontSize,
              fontWeight: weightMap[state.labelFontWeight] ?? 500,
              lineHeight: '24px',
            }}
          >
            {label}
          </span>
        )}
      </button>

      {/* Tooltip – only when collapsed & hovered */}
      {!isExpanded && isHovered && (
        <div
          className="tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
          style={{
            backgroundColor: resolveColor(state.tooltipBgColor),
            color: resolveColor(state.tooltipTextColor),
            fontSize: state.tooltipFontSize,
            fontWeight: 600,
            lineHeight: '18px',
            borderRadius: state.tooltipBorderRadius,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Logo with hover tooltip (collapsed: shows brand link; expanded: shows link next to logo)
// ---------------------------------------------------------------------------

interface LogoWithTooltipProps {
  state: ConciergeSidebarWidgetState;
  icons: ConciergeSidebarResolvedIcons;
  resolveColor: (c: any, d?: string) => string;
  isExpanded: boolean;
  brandLogoUrl?: string;
  onToggle?: () => void;
  onClick?: () => void;
  justCollapsedRef?: React.MutableRefObject<boolean>;
  sidebarHovered?: boolean;
}

const LogoWithTooltip = ({
  state,
  icons,
  resolveColor,
  isExpanded,
  brandLogoUrl,
  onToggle,
  onClick,
  sidebarHovered,
  justCollapsedRef,
}: LogoWithTooltipProps) => {
  const showToggleIcon = !isExpanded && !!sidebarHovered && !justCollapsedRef?.current;
  const [iconHovered, setIconHovered] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <div
      className={`tw:flex tw:items-center tw:shrink-0 tw:pt-[20px] tw:px-[12px] tw:pb-[24px] ${
        isExpanded ? 'tw:justify-between' : ''
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Collapsed: logo or sidebar toggle icon */}
      {!isExpanded && (
        <div className="tw:relative">
          <button
            onMouseEnter={() => setIconHovered(true)}
            onMouseLeave={() => setIconHovered(false)}
            className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-lg tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0"
            // This one button is two controls depending on hover state, so the
            // name has to follow the action, not the icon. "Home" on its own
            // gave no hint of where it goes.
            aria-label={showToggleIcon ? state.openSidebarLabel : 'Concierge home'}
            onClick={(e) => {
              e.stopPropagation();
              if (showToggleIcon) {
                onToggle?.();
              } else {
                onClick?.();
              }
            }}
          >
            {showToggleIcon ? (
              <IconSidebar size={state.sidebarIconSize} style={{ color: resolveColor(state.sidebarIconColor) }} />
            ) : (icons.logoIconUrl || brandLogoUrl) && !logoFailed ? (
              <img
                src={icons.logoIconUrl || brandLogoUrl}
                alt="Brand"
                className="tw:object-contain"
                // `getBrandLogo(brandId)` builds this URL from the id alone and
                // nothing guarantees the asset exists, so the truthiness check
                // above can never catch a missing logo. Fall through to the icon.
                onError={() => setLogoFailed(true)}
                style={{
                  height: state.logoIconSize,
                  width: state.logoIconSize,
                }}
              />
            ) : (
              <IconHome size={state.logoIconSize} style={{ color: resolveColor(state.sidebarIconColor) }} />
            )}
          </button>

          {showToggleIcon && iconHovered && (
            <div
              className="tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
              style={{
                backgroundColor: resolveColor(state.tooltipBgColor),
                color: resolveColor(state.tooltipTextColor),
                fontSize: state.tooltipFontSize,
                fontWeight: 600,
                lineHeight: '18px',
                borderRadius: state.tooltipBorderRadius,
              }}
            >
              {state.openSidebarLabel}
            </div>
          )}
        </div>
      )}

      {/* Expanded: logo on left, sidebar toggle on right */}
      {isExpanded && (
        <>
          <button
            className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0"
            aria-label="Concierge home"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            {icons.logoIconUrl || brandLogoUrl ? (
              <img
                src={icons.logoIconUrl || brandLogoUrl}
                alt="Brand"
                className="tw:object-contain"
                style={{
                  height: state.logoIconSize,
                  width: state.logoIconSize,
                }}
              />
            ) : (
              <IconHome size={state.logoIconSize} style={{ color: resolveColor(state.sidebarIconColor) }} />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            aria-label={state.closeSidebarLabel}
            className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:shrink-0 tw:border-none tw:bg-transparent tw:p-0"
          >
            <IconSidebar size={state.sidebarIconSize} style={{ color: resolveColor(state.closeIconColor) }} />
          </button>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Chat history item
// ---------------------------------------------------------------------------

interface HistoryItemProps {
  conversation: ConversationItem;
  isActive: boolean;
  /** True when this conversation is currently in a live support takeover. */
  isInLiveTakeover?: boolean;
  onSelect: (id: string, mode?: 'text' | 'voice') => void;
  onDelete: (id: string) => Promise<void>;
  onShareChat?: (conversationId: string) => void;
  /** When true, this row shows an inline input to edit the conversation title (ChatGPT-style) */
  isEditingTitle: boolean;
  editingTitleValue: string;
  onStartEditTitle: (conversationId: string, title: string) => void;
  onSaveEditTitle: (conversationId: string, newTitle: string) => Promise<void>;
  onCancelEditTitle: () => void;
  state: ConciergeSidebarWidgetState;
  resolveColor: (c: any, d?: string) => string;
}

const HistoryItem = ({
  conversation,
  isActive,
  isInLiveTakeover,
  onSelect,
  onDelete,
  onShareChat,
  isEditingTitle,
  editingTitleValue,
  onStartEditTitle,
  onSaveEditTitle,
  onCancelEditTitle,
  state,
  resolveColor,
}: HistoryItemProps) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [localEditValue, setLocalEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dotBtnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const isMobile = useIsMobile();

  const isThisEditing = isEditingTitle;
  // On touch devices there is no hover — keep the kebab always visible so users
  // can reach share/rename/delete without a long-press.
  const showKebab = (hovered || menuOpen || isMobile) && !deleting;

  // Derived-state transition: when this row enters edit mode, seed the local
  // input value from the prop *during render* (not in an effect) so the input
  // mounts with the full title on first paint. Without this, the input briefly
  // renders with value="" and iOS Safari skips the selection highlight even if
  // setSelectionRange fires later in a setTimeout.
  const [lastEditingId, setLastEditingId] = useState<string | null>(null);
  const currentEditingId = isThisEditing ? conversation.conversationId : null;
  if (currentEditingId !== lastEditingId) {
    setLastEditingId(currentEditingId);
    setLocalEditValue(currentEditingId ? editingTitleValue : '');
  }

  useEffect(() => {
    if (!isThisEditing) return;
    const input = editInputRef.current;
    if (!input) return;
    // Value is already committed (see derived-state transition above), so we
    // can focus + select synchronously — which iOS Safari requires to show
    // the selection highlight. onFocus on the <input> handles any re-focus.
    input.focus();
    input.setSelectionRange(0, input.value.length);
  }, [isThisEditing]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const openMenu = useCallback(() => {
    if (dotBtnRef.current) {
      const rect = dotBtnRef.current.getBoundingClientRect();
      // Estimated menu size — matches ConversationActionMenu's minWidth and the
      // rendered 3-item height. Used to clamp the position within the viewport
      // so the portaled (position:fixed) menu never pokes outside. An
      // off-screen fixed element extends the document's scroll extent on iOS
      // Safari, which causes a large white space at the bottom of the page
      // and makes the menu appear to drift from its row during any drag.
      const MENU_WIDTH = 160;
      const MENU_HEIGHT = 120;
      const EDGE_GUTTER = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Drop the menu below the kebab and anchor its left edge to the button's
      // left edge so it extends toward the lower-right (matches desktop). On
      // narrow viewports the min() clamp pulls it back inside the viewport.
      const preferredLeft = rect.left;
      const clampedLeft = Math.max(EDGE_GUTTER, Math.min(preferredLeft, vw - MENU_WIDTH - EDGE_GUTTER));
      const preferredTop = rect.bottom + 4;
      const clampedTop = Math.min(preferredTop, vh - MENU_HEIGHT - EDGE_GUTTER);
      setDropdownPos({ top: clampedTop, left: clampedLeft });
    }
    setMenuOpen((p) => !p);
  }, []);

  return (
    <div
      className="tw:relative"
      // On touch devices, `onMouseEnter` fires on tap with no matching
      // `onMouseLeave` until another tap occurs — that leaves `hovered: true`
      // sticky on every previously-tapped row and makes multiple rows look
      // selected at once. Skip mouse-driven hover state entirely on mobile;
      // the kebab is always visible there, so we don't need a hover signal.
      onMouseEnter={isMobile ? undefined : () => setHovered(true)}
      onMouseLeave={
        isMobile
          ? undefined
          : () => {
              setHovered(false);
              if (!menuOpen) setMenuOpen(false);
            }
      }
    >
      <button
        onClick={() => !deleting && !isThisEditing && onSelect(conversation.conversationId, conversation.mode)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: `${state.chatItemPaddingY}px ${state.chatItemPaddingX}px`,
          paddingRight: showKebab || isThisEditing ? 32 : state.chatItemPaddingX,
          backgroundColor:
            isActive || isThisEditing
              ? resolveColor(state.chatItemActiveBgColor, 'rgba(220,221,220,0.5)')
              : !isMobile && (hovered || menuOpen)
              ? resolveColor(state.chatItemHoverBgColor, 'rgba(220,221,220,0.3)')
              : 'transparent',
          border: 'none',
          cursor: deleting ? 'default' : 'pointer',
          transition: 'background-color 0.15s ease, opacity 0.2s ease',
          borderRadius: state.chatItemBorderRadius,
          display: 'block',
          opacity: deleting ? 0.5 : 1,
        }}
      >
        <div
          className="tw:flex tw:items-center tw:gap-2"
          style={{
            fontSize: state.chatItemFontSize,
            fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
            color: resolveColor(state.chatItemColor),
            lineHeight: '24px',
          }}
        >
          {deleting && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="tw:shrink-0"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke={resolveColor(state.chatItemColor)}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="32"
                strokeDashoffset="12"
              />
            </svg>
          )}
          {isThisEditing ? (
            <input
              ref={editInputRef}
              type="text"
              value={localEditValue}
              onChange={(e) => setLocalEditValue(e.target.value)}
              onBlur={() => {
                const trimmed = localEditValue.trim();
                if (trimmed && trimmed !== (conversation.title || conversation.preview)) {
                  onSaveEditTitle(conversation.conversationId, trimmed);
                } else {
                  onCancelEditTitle();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const trimmed = localEditValue.trim();
                  if (trimmed) {
                    onSaveEditTitle(conversation.conversationId, trimmed);
                  } else {
                    onCancelEditTitle();
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEditTitle();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => {
                // Select-all on any focus so the user sees a clear "ready to
                // overtype" state — matches the desktop rename UX.
                e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
              }}
              style={{
                width: '100%',
                flex: 1,
                minWidth: 0,
                padding: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: state.chatItemFontSize,
                fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                color: resolveColor(state.chatItemColor),
                lineHeight: '24px',
              }}
              aria-label="Edit conversation name"
            />
          ) : (
            <>
              {isInLiveTakeover && (
                <span
                  className="tw:relative tw:flex tw:h-1.5 tw:w-1.5 tw:shrink-0"
                  aria-label="Connected to support team"
                >
                  {/* D-33 — both layers use `--bc-support-dot`; the ping ring's
                      built-in opacity animation gives it the lighter look the
                      previous emerald-400 ring produced. */}
                  <span
                    className="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-ping tw:rounded-full tw:opacity-75"
                    style={{ backgroundColor: 'var(--bc-support-dot)' }}
                  />
                  <span
                    className="tw:relative tw:inline-flex tw:h-1.5 tw:w-1.5 tw:rounded-full"
                    style={{ backgroundColor: 'var(--bc-support-dot)' }}
                  />
                </span>
              )}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: state.chatItemLineClamp,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {conversation.title || conversation.preview || 'New chat'}
              </span>
            </>
          )}
        </div>
      </button>

      {showKebab && (
        <div className="tw:absolute tw:right-1 tw:top-1/2 tw:-translate-y-1/2">
          <button
            ref={dotBtnRef}
            onClick={(e) => {
              e.stopPropagation();
              openMenu();
            }}
            className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/10 tw:transition-colors"
            // One "More options" per row is indistinguishable in a screen
            // reader's control list — name the conversation it belongs to.
            aria-label={`More options for conversation: ${
              conversation.title || conversation.preview || 'New chat'
            }`}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="3" r="1.5" fill={resolveColor(state.chatItemColor)} />
              <circle cx="8" cy="8" r="1.5" fill={resolveColor(state.chatItemColor)} />
              <circle cx="8" cy="13" r="1.5" fill={resolveColor(state.chatItemColor)} />
            </svg>
          </button>
        </div>
      )}

      {menuOpen &&
        dropdownPos &&
        ReactDOM.createPortal(
          <ConversationActionMenu
            ref={menuRef}
            fontFamily={(state as any).fontFamilyCss}
            items={buildConversationMenuItems({
              onShare: () => {
                setMenuOpen(false);
                onShareChat?.(conversation.conversationId);
              },
              onRename: () => {
                setMenuOpen(false);
                onStartEditTitle(conversation.conversationId, conversation.title || conversation.preview || '');
              },
              onDelete: () => {
                setMenuOpen(false);
                setDeleteConfirmOpen(true);
              },
              defaultColor: resolveColor(state.chatItemColor),
            })}
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          />,
          document.body,
        )}
      <ConfirmActionDialog
        open={deleteConfirmOpen}
        title="Are you sure? This action cannot be undone."
        message={<>Continuing will delete <strong>{conversation.title || 'this conversation'}</strong>.</>}
        confirmText="Delete"
        // Brand theme: dialog surface = sidebar bg, primary = chat-item text
        // (navy on PetArmor), foreground = contrast text used by tooltips.
        backgroundColor={resolveColor(state.expandedBgColor)}
        primaryColor={resolveColor(state.chatItemColor)}
        primaryForegroundColor={resolveColor(state.tooltipTextColor)}
        fontFamily={(state as any).fontFamilyCss}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={async () => {
          setDeleteConfirmOpen(false);
          setDeleting(true);
          try {
            await onDelete(conversation.conversationId);
          } finally {
            setDeleting(false);
          }
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Profile menu items (shared between expanded & collapsed dropdowns)
// ---------------------------------------------------------------------------

interface ProfileMenuItemsProps {
  onProfileClick?: () => void;
  onPurchasesClick?: () => void;
  onSignOutClick?: () => void;
  signOutLabel: string;
  labelColor: any;
  resolveColor: (c: any, d?: string) => string;
  onClose: () => void;
}

const ProfileMenuItems: React.FC<ProfileMenuItemsProps> = ({
  onProfileClick,
  onPurchasesClick,
  onSignOutClick,
  signOutLabel,
  labelColor,
  resolveColor,
  onClose,
}) => {
  const color = resolveColor(labelColor);
  const itemStyle = { gap: 8, padding: '4px', fontFamily: 'inherit' } as const;
  const labelStyle = { fontSize: 14, fontWeight: 500, color, lineHeight: '24px', whiteSpace: 'nowrap' } as const;


  return (
    <div className="tw:flex tw:flex-col" style={{ padding: '4px' }}>
      {onProfileClick && (
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); onProfileClick(); }}
          className="tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md"
          style={itemStyle}
        >
          <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0" style={{ width: 24, height: 24 }}>
            <IconProfile size={18} style={{ color }} />
          </div>
          <span style={labelStyle}>Profile</span>
        </button>
      )}
      {onPurchasesClick && (
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); onPurchasesClick(); }}
          className="tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md"
          style={itemStyle}
        >
          <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0" style={{ width: 24, height: 24 }}>
            <IconPurchases size={18} style={{ color }} />
          </div>
          <span style={labelStyle}>Purchases</span>
        </button>
      )}
      <div style={{ height: 0, borderTop: '1px solid #e8e8ed', margin: '4px -4px' }} />
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); onSignOutClick?.(); }}
        className="tw:flex tw:items-center tw:border-none tw:cursor-pointer tw:bg-transparent tw:hover:bg-black/5 tw:transition-colors tw:rounded-md"
        style={itemStyle}
      >
        <div className="tw:flex tw:items-center tw:justify-center tw:shrink-0" style={{ width: 24, height: 24 }}>
          <IconLogout size={18} style={{ color }} />
        </div>
        <span style={labelStyle}>{signOutLabel}</span>
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ConciergeSidebar: React.FC<ConciergeSidebarProps> = ({
  onConversationSelect,
  onComposeClick,
  onChatsClick,
  currentConversationId,
  userProfile,
  apiBaseUrl,
  onSignOutClick,
  onProfileClick,
  onPurchasesClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle,
  mobileOverlay = false,
  onShareChat,
  onSignInClick,
  liveTakeoverActiveConversationId,
}) => {
  const isMobile = useIsMobile();
  const conciergeContext = useOptionalConciergeContext();
  const resolvedApiBaseUrl = apiBaseUrl ?? conciergeContext?.apiBaseUrl ?? resolveBrandclubHostDomain();
  const { data: domainConfig } = useDomainConfig();
  const { data: brandAppConfig } = useBrandAppConfig(resolvedApiBaseUrl);
  const studioSupportSkills = useStudioSupportSkills();
  // An OPT-OUT, not an opt-in. `=== true` meant a project with no `supportSkills`
  // row at all — every app hydrated before D-45 — hid the sign-in button even
  // under `shopify`/`stackline`, where the shopper plainly can sign in. Whether
  // the app HAS a sign-in is `signInAvailable`'s job (the auth type); this flag
  // only lets an operator deliberately suppress it.
  const canShowCustomerLogin = studioSupportSkills?.customerLogin !== false;
  const brandLinkTextFromConfig = (() => {
    if (domainConfig?.customShopDomainName) {
      return domainConfig.customShopDomainName;
    }
    const link = brandAppConfig?.branchIOConfig?.webAppBranchLink;
    if (!link) return undefined;
    try {
      return new URL(link).hostname;
    } catch {
      return link.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    }
  })();

  // Controlled / uncontrolled expansion
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = isExpandedProp !== undefined;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;

  const justCollapsedRef = useRef(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleToggle = () => {
    if (isExpanded) {
      justCollapsedRef.current = true;
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = setTimeout(() => {
        justCollapsedRef.current = false;
      }, 400);
    }
    if (isControlled) onToggle?.();
    else setInternalExpanded((p) => !p);
  };

  // Unified conversation list from shared hook
  const {
    conversations,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    deleteMutation,
    renameMutation,
    refresh: refreshConversations,
  } = useConversations({ apiBaseUrl: resolvedApiBaseUrl });

  const STALE_THRESHOLD_MS = 5 * 60_000;
  const shouldPollConversations = conversations.some((c) => {
    if (c.status !== 'in_progress') return false;
    const age = Date.now() - new Date(c.lastUpdated).getTime();
    return age < STALE_THRESHOLD_MS;
  });

  useEffect(() => {
    if (!isExpanded || !shouldPollConversations) return;
    const intervalId = window.setInterval(() => refreshConversations(), 3000);
    return () => window.clearInterval(intervalId);
  }, [isExpanded, shouldPollConversations, refreshConversations]);

  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || !isExpanded || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await deleteMutation.mutateAsync([conversationId]);

        AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_CONVERSATION_DELETE, {
          conversationId,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          appVersion: 'v2',
        }).catch(() => {});

        if (currentConversationId === conversationId) {
          onComposeClick?.();
        }
      } catch {
        // silently fail
      }
    },
    [deleteMutation, currentConversationId, onComposeClick],
  );

  // Inline edit conversation title (ChatGPT-style)
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');

  const handleStartEditTitle = useCallback((conversationId: string, title: string) => {
    setEditingConversationId(conversationId);
    setEditingTitleValue(title);
  }, []);

  const handleSaveEditTitle = useCallback(
    async (conversationId: string, newTitle: string) => {
      setEditingConversationId(null);
      setEditingTitleValue('');
      try {
        await renameMutation.mutateAsync({ conversationId, title: newTitle });
      } catch {
        // silently fail — optimistic update is rolled back via onError in the hook
      }
    },
    [renameMutation],
  );

  const handleCancelEditTitle = useCallback(() => {
    setEditingConversationId(null);
    setEditingTitleValue('');
  }, []);

  // User avatar helpers
  const getInitials = () => {
    const f = userProfile?.firstName || '';
    const l = userProfile?.lastName || '';
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    // Two letters from the email, not one: a single letter in a circle reads as
    // a rendering glitch rather than an identity. Take them from the local part
    // only, so "a@b.com" yields "A" rather than "A@".
    const local = (userProfile?.email || '').split('@')[0];
    if (local) return local.slice(0, 2).toUpperCase();
    return '';
  };
  const initials = getInitials();
  const displayName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || '';
  const isSignedIn = userProfile?.isSignedIn ?? false;

  // Whether this app has a sign-in to offer at all (D-42). `no_auth` apps have
  // none — a "Sign in" button there leads nowhere — so the card is hidden.
  //
  // Only a LOADED config hides it, so front-room brands never see it flicker
  // off while the bootstrap is in flight. `authType` is absent on the V1 config
  // path, hence the fall back to "is there a Cognito block", which is what this
  // gate used to be.
  const { data: sidebarAppConfig, isSuccess: appConfigLoaded } = useAppConfig();
  const signInAvailable =
    !appConfigLoaded ||
    (sidebarAppConfig?.authType
      ? sidebarAppConfig.authType !== "no_auth"
      : Boolean(sidebarAppConfig?.authConfig));

  const prevIsSignedInRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    prevIsSignedInRef.current = isSignedIn;

    if (wasSignedIn === false && isSignedIn) {
      refreshConversations();
    }
  }, [isSignedIn, refreshConversations]);

  const [avatarHovered, setAvatarHovered] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileBtnRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [profileMenuPos, setProfileMenuPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target as Node) &&
        profileBtnRef.current &&
        !profileBtnRef.current.contains(e.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen]);

  return (
    <ConciergeSidebarWidget
      widgetId="concierge-sidebar"
      displayName="Concierge Sidebar"
      wrapperStyle={{
        width: 'auto',
        flexShrink: 0,
        display: 'flex',
        height: '100%',
      }}
    >
      {(
        widgetState: ConciergeSidebarWidgetState & { fontFamilyCss?: string },
        icons: ConciergeSidebarResolvedIcons,
        resolveColor,
      ) => {
        const state = brandLinkTextFromConfig
          ? { ...widgetState, brandLinkText: brandLinkTextFromConfig }
          : widgetState;
        return (
          <aside
            role="complementary"
            aria-expanded={isExpanded}
            onClick={!isExpanded ? handleToggle : undefined}
            onMouseEnter={() => !isExpanded && setSidebarHovered(true)}
            onMouseLeave={() => setSidebarHovered(false)}
            className={
              mobileOverlay
                ? 'tw:flex tw:flex-col tw:shrink-0 tw:h-full'
                : 'tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full'
            }
            style={{
              backgroundColor: resolveColor(isExpanded ? state.expandedBgColor : state.collapsedBgColor),
              width: isExpanded ? state.expandedWidth : state.collapsedWidth,
              transition: 'width 0.25s ease-in-out',
              cursor: isExpanded ? undefined : 'pointer',
              borderRight: `1px solid ${resolveColor(state.borderColor)}`,
              boxSizing: 'content-box',
              overflow: 'hidden',
              ...(state.fontFamilyCss && state.fontFamilyCss !== 'inherit' ? { fontFamily: state.fontFamilyCss } : {}),
            }}
          >
            {/* ── Top row: brand logo + sidebar toggle ─────────────── */}
            <LogoWithTooltip
              state={state}
              icons={icons}
              resolveColor={resolveColor}
              isExpanded={isExpanded}
              brandLogoUrl={brandLogoUrl}
              onToggle={handleToggle}
              onClick={onComposeClick}
              sidebarHovered={sidebarHovered}
              justCollapsedRef={justCollapsedRef}
            />

            {/* ── Navigation buttons ────────────────────────────────── */}
            <div className="tw:flex tw:flex-col">
              {/* New chat */}
              <SidebarButton
                icon={
                  <IconCompose size={state.composeIconSize} style={{ color: resolveColor(state.composeIconColor) }} />
                }
                label={state.newChatLabel}
                onClick={onComposeClick}
                isExpanded={isExpanded}
                ariaLabel={state.newChatLabel}
                state={state}
                resolveColor={resolveColor}
              />

              {/* Chats */}
              <SidebarButton
                icon={<IconChats size={state.chatsIconSize} style={{ color: resolveColor(state.chatsIconColor) }} />}
                label={state.chatsLabel}
                onClick={onChatsClick}
                isExpanded={isExpanded}
                // "Chats" alone does not say what activating it does. The
                // accessible name still opens with the visible label, which
                // WCAG 2.5.3 (label in name) requires.
                ariaLabel={`${state.chatsLabel}: view your conversation history`}
                state={state}
                resolveColor={resolveColor}
              />
            </div>

            {/* ── Chat history (expanded) / Spacer (collapsed) ──────── */}
            {isExpanded ? (
              <div className="sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden tw:mt-[24px]">
                {/* Hide entire section when no conversations */}
                {(isLoading || conversations.length > 0) && (
                  <div
                    className="tw:px-4 tw:pb-1 tw:shrink-0"
                    style={{
                      fontSize: state.chatHistoryTitleFontSize,
                      fontWeight: weightMap[state.chatHistoryTitleFontWeight] ?? 500,
                      color: resolveColor(state.chatHistoryTitleColor),
                      lineHeight: '24px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {state.chatHistoryTitle}
                  </div>
                )}

                <div className="tw:flex-1 tw:overflow-y-auto tw:mt-1 tw:px-2 concierge-conversation-scroll">
                  {isLoading && (
                    <div className="tw:flex tw:justify-center tw:py-4">
                      <LoadingSpinner color={resolveColor(state.chatHistoryTitleColor)} />
                    </div>
                  )}

                  {!isLoading && conversations.length > 0 && (
                    <div className="sidebar-history-list">
                      {conversations.map((c) => (
                        <HistoryItem
                          key={c.conversationId}
                          conversation={c}
                          isActive={c.conversationId === currentConversationId}
                          isInLiveTakeover={c.conversationId === liveTakeoverActiveConversationId}
                          onSelect={(id, mode) => onConversationSelect?.(id, mode)}
                          onDelete={handleDeleteConversation}
                          onShareChat={onShareChat}
                          isEditingTitle={editingConversationId === c.conversationId}
                          editingTitleValue={editingTitleValue}
                          onStartEditTitle={handleStartEditTitle}
                          onSaveEditTitle={handleSaveEditTitle}
                          onCancelEditTitle={handleCancelEditTitle}
                          state={state}
                          resolveColor={resolveColor}
                        />
                      ))}
                      {hasNextPage && (
                        <div
                          ref={loadMoreSentinelRef}
                          className="tw:flex tw:justify-center tw:py-2"
                          style={{ minHeight: 1 }}
                        >
                          {isFetchingNextPage && <LoadingSpinner color={resolveColor(state.chatHistoryTitleColor)} />}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="tw:flex-1" />
            )}

            {/* ── User avatar + profile menu / Sign-in link ────────── */}
            {isSignedIn ? (
              <div
                className="tw:relative tw:mt-auto tw:shrink-0"
                onClick={(e) => e.stopPropagation()}
                style={{
                  borderTop: isExpanded ? `1px solid ${resolveColor(state.borderColor)}` : undefined,
                  backgroundColor: isExpanded ? resolveColor(state.expandedBgColor) : undefined,
                }}
              >
                {/* Profile dropdown menu (expanded – positioned within sidebar) */}
                {profileMenuOpen && isExpanded && (
                  <div
                    ref={profileMenuRef}
                    className="tw:absolute tw:bottom-full tw:z-50"
                    style={{
                      left: 16,
                      marginBottom: 8,
                      width: 216,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e8e8ed',
                      borderRadius: 5,
                      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.05)',
                      fontFamily: 'inherit',
                    }}
                  >
                    <ProfileMenuItems
                      onProfileClick={onProfileClick}
                      onPurchasesClick={onPurchasesClick}
                      onSignOutClick={onSignOutClick}
                      signOutLabel={state.signOutLabel}
                      labelColor={state.labelColor}
                      resolveColor={resolveColor}
                      onClose={() => setProfileMenuOpen(false)}
                    />
                  </div>
                )}

                <button
                  ref={profileBtnRef}
                  // A bare customer name reads as a label, not a control — it
                  // gave no clue that activating it opens the account menu.
                  aria-label={
                    displayName ? `Account menu for ${displayName}` : `Account menu (${state.signOutLabel})`
                  }
                  aria-expanded={profileMenuOpen}
                  aria-haspopup="menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isExpanded && profileBtnRef.current) {
                      const rect = profileBtnRef.current.getBoundingClientRect();
                      setProfileMenuPos({ top: rect.top - 4, left: rect.left + 4 });
                    }
                    setProfileMenuOpen((p) => !p);
                  }}
                  onMouseEnter={() => setAvatarHovered(true)}
                  onMouseLeave={() => setAvatarHovered(false)}
                  className={`tw:flex tw:items-center tw:cursor-pointer tw:w-full tw:rounded-none tw:transition-colors hover:tw:bg-black/5 tw:border-none tw:bg-transparent tw:overflow-hidden ${
                    isExpanded ? 'tw:h-16 tw:px-4 tw:gap-3' : 'tw:py-2 tw:px-2 tw:mb-2'
                  }`}
                  // Override tw:h-16 (64px) → 65px on mobile responsive.
                  style={isMobile && isExpanded ? { height: 65 } : undefined}
                >
                  {/* Avatar circle */}
                  <div
                    className="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0"
                    style={{
                      width: state.avatarSize,
                      height: state.avatarSize,
                      backgroundColor: resolveColor(state.avatarBgColor),
                      color: resolveColor(state.avatarTextColor),
                      fontSize: state.avatarFontSize,
                      fontWeight: weightMap[state.avatarFontWeight] ?? 600,
                      lineHeight: '1.5',
                    }}
                  >
                    {/* No name AND no email — a Shopify guest-checkout customer
                        can have both null. An empty circle looks broken, so fall
                        back to the profile glyph rather than rendering nothing. */}
                    {initials || <IconProfile size={Math.round(state.avatarSize * 0.6)} />}
                  </div>

                  {/* User name (expanded) */}
                  {isExpanded && (
                    <span
                      className="tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis tw:flex-1 tw:text-left"
                      style={{
                        color: resolveColor(state.labelColor),
                        fontSize: 14,
                        fontWeight: weightMap[state.labelFontWeight] ?? 500,
                        lineHeight: '24px',
                      }}
                    >
                      {displayName || state.signOutLabel}
                    </span>
                  )}

                  {/* Chevron (expanded) */}
                  {isExpanded && (
                    <IconChevronUp
                      size={18}
                      style={{
                        color: resolveColor(state.labelColor),
                        transform: profileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </button>

                {/* Tooltip for avatar when collapsed */}
                {!isExpanded && avatarHovered && !profileMenuOpen && (
                  <div
                    className="tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
                    style={{
                      backgroundColor: resolveColor(state.tooltipBgColor),
                      color: resolveColor(state.tooltipTextColor),
                      fontSize: state.tooltipFontSize,
                      fontWeight: 600,
                      lineHeight: '18px',
                      borderRadius: state.tooltipBorderRadius,
                    }}
                  >
                    {displayName || state.signOutLabel}
                  </div>
                )}

                {/* Profile dropdown menu (collapsed – portal) */}
                {profileMenuOpen &&
                  !isExpanded &&
                  profileMenuPos &&
                  ReactDOM.createPortal(
                    <div
                      ref={profileMenuRef}
                      className="tw:fixed tw:z-[9999]"
                      style={{
                        top: profileMenuPos.top,
                        left: profileMenuPos.left,
                        transform: 'translateY(-100%)',
                        width: 216,
                        backgroundColor: '#ffffff',
                        border: '1px solid #e8e8ed',
                        borderRadius: 5,
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.05)',
                        fontFamily:
                          (state as any).fontFamilyCss && (state as any).fontFamilyCss !== 'inherit'
                            ? (state as any).fontFamilyCss
                            : undefined,
                      }}
                    >
                      <ProfileMenuItems
                        onProfileClick={onProfileClick}
                        onPurchasesClick={onPurchasesClick}
                        onSignOutClick={onSignOutClick}
                        signOutLabel={state.signOutLabel}
                        labelColor={state.labelColor}
                        resolveColor={resolveColor}
                        onClose={() => setProfileMenuOpen(false)}
                      />
                    </div>,
                    document.body,
                  )}
              </div>
            ) : canShowCustomerLogin && signInAvailable ? (
              <div
                className="tw:relative tw:mt-auto tw:shrink-0"
                onClick={(e) => e.stopPropagation()}
                style={{
                  borderTop: isExpanded ? '1px solid #dcdddc' : undefined,
                  backgroundColor: isExpanded ? resolveColor(state.expandedBgColor) : undefined,
                }}
              >
                {/* Collapsed: an icon button, mirroring the signed-IN avatar.
                    The whole signed-out block used to sit inside `isExpanded`,
                    so a signed-out shopper on a collapsed sidebar — the default —
                    had NO way to sign in anywhere on the page. Signed IN rendered
                    its avatar collapsed and expanded both; only the signed-out
                    half was hidden. The asymmetry was the bug. */}
                {!isExpanded && (
                  <>
                    <button
                      aria-label="Sign in"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSignInClick?.();
                      }}
                      onMouseEnter={() => setAvatarHovered(true)}
                      onMouseLeave={() => setAvatarHovered(false)}
                      className="tw:flex tw:items-center tw:cursor-pointer tw:w-full tw:rounded-none tw:transition-colors hover:tw:bg-black/5 tw:border-none tw:bg-transparent tw:overflow-hidden tw:py-2 tw:px-2 tw:mb-2"
                    >
                      <div
                        className="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0"
                        style={{
                          width: state.avatarSize,
                          height: state.avatarSize,
                          backgroundColor: resolveColor(state.avatarBgColor),
                          color: resolveColor(state.avatarTextColor),
                        }}
                      >
                        <IconProfile size={18} />
                      </div>
                    </button>
                    {avatarHovered && (
                      <div
                        className="tw:absolute tw:left-[53px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap"
                        style={{
                          backgroundColor: resolveColor(state.tooltipBgColor),
                          color: resolveColor(state.tooltipTextColor),
                          fontSize: state.tooltipFontSize,
                          fontWeight: 600,
                          lineHeight: '18px',
                          borderRadius: state.tooltipBorderRadius,
                        }}
                      >
                        Sign in
                      </div>
                    )}
                  </>
                )}
                {isExpanded && (
                  <div
                    className="tw:flex tw:flex-col tw:items-stretch"
                    style={{
                      padding: '20px 12px',
                      width: state.expandedWidth - 1,
                      boxSizing: 'border-box',
                    }}
                  >
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: resolveColor(state.labelColor),
                        lineHeight: '24px',
                        margin: '0 0 16px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Get responses tailored to you
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: resolveColor(state.labelColor),
                        lineHeight: '18px',
                        margin: '0 0 24px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Log in to access your profile and
                      <br />
                      get personalized assistance.
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSignInClick?.();
                      }}
                      className="tw:cursor-pointer tw:border tw:border-solid tw:border-[#dcdddc] tw:rounded-full tw:transition-all tw:duration-200 tw:bg-white"
                      style={{
                        padding: '8px 12px',
                        fontSize: 14,
                        fontWeight: 600,
                        color: resolveColor(state.labelColor),

                        fontFamily: 'inherit',
                        lineHeight: '20px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Sign in
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </aside>
        );
      }}
    </ConciergeSidebarWidget>
  );
};

// ---------------------------------------------------------------------------
// Loading spinner (inline, avoids importing from deleted HistoryList)
// ---------------------------------------------------------------------------

const LoadingSpinner: React.FC<{ color: string }> = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="32"
      strokeDashoffset="12"
    />
  </svg>
);
